import { useLocalization } from '@fluent/react'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useEffect, useState } from 'react'
import { Device, loadDevicesForSupplier } from './deviceSlice'
import { Box } from '@mui/material'
import { loadDeviceTypes, selectDeviceTypes } from './deviceTypesSlice'
import { FlexDiv } from '../../app/Utils'
import { selectModule } from "../modules/moduleSlice";
import { selectSettings } from '../settings/settingsSlice'
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { AddFabButton } from "../../app/components/AddFabButton";
import { CreateDeviceDialog } from './dialogs/DeviceCreateEditDialog'
import { StructureName } from './Structure'
import { QuickSearchToolbar } from '../../app/QuickSearchToolbar'
import { entityRoutes } from '../../app/components/EntityBreadcrumbs'
import { unwrapResult } from '@reduxjs/toolkit'
import { LoadingContainer } from '../../app/LoadingContainer'
import { ErrorContainer } from '../../app/ErrorContainer'
import { GoToButton } from '../../app/components/GoToButton'

interface DevicesForSupplierProps {
    supplierId: string;
}

export const DevicesForSupplier = ({ supplierId }: DevicesForSupplierProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const [devices, setDevices] = useState<Device[]>([]);
    const { state: deviceTypesState } = useAppSelector(selectDeviceTypes)
    const currentModule =
        useAppSelector(state => selectModule(state).currentModule)

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);

    const [openCreate, setOpenCreate] = useState(false);
    const handleOpen = () => setOpenCreate(true);
    const handleCancel = () => setOpenCreate(false);


    useEffect(() => {
        if (currentModule !== null) {
            setIsLoading(true);

            dispatch(loadDevicesForSupplier({ moduleId: currentModule.sysModuleId, supplierId }))
                .then(unwrapResult)
                .then(setDevices)
                .catch((error) => {
                    console.error(error);
                    setIsError(true);
                    setError(error);
                })
                .finally(() => setIsLoading(false));
        }

        if (deviceTypesState.type === "empty") {
            dispatch(loadDeviceTypes())
        }

    }, [dispatch, deviceTypesState, currentModule, supplierId])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleSuccess = (device: Device): void => {
        setDevices(prev => [...prev, device])
        handleCancel();
    }

    const GoToDeviceForm = (id) => {
        navigate(entityRoutes.device(id))
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: l10n.getString("devices-name", null, "Nazwa"),
            flex: 1
        },
        {
            field: 'structureId',
            headerName: l10n.getString("structure", null, "_structure"),
            flex: 1,
            minWidth: 200,

            renderCell: (params) => {
                return (
                    <StructureName structureId={params.row.structureId} />
                )
            }
        },
        {
            field: 'serialNo',
            headerName: l10n.getString("devices-serial-number", null, "Nr fabryczny"),
            minWidth: 200,
            valueGetter: (_, row) => {
                const unknownNo = row.unknownNo;
                const serialNo = row.serialNo;
                return unknownNo ? l10n.getString("device-unknown-no", null, "_Nieznany numer fabryczny") : serialNo || "—";
            }
        },
        {
            field: 'productionYear',
            headerName: l10n.getString("devices-production-year", null, "Rok produkcji"),
            width: 150
        },
        {
            field: 'actions', type: 'actions', width: 75,
            renderCell: (params) => {
                return (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <GoToButton onClick={() => GoToDeviceForm(params.row.id)} />
                    </Box>
                )
            }
        }
    ]

    if(isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    if(isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid
                    localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 475px)' }}
                    rows={devices}
                    columns={columns}
                    slots={{ toolbar: QuickSearchToolbar }}
                    onRowDoubleClick={({ row }) => GoToDeviceForm(row.id)}

                    initialState={{
                        sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
                    }}
                />
                <FlexDiv>
                    <AddFabButton
                        onClick={handleOpen}
                    />
                </FlexDiv>
            </TableContainer>
            {openCreate && (
                <CreateDeviceDialog
                    supplierId={supplierId}
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                />
            )}
        </>
    );
}
